//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { useState } from 'react';
import styled, { useTheme } from 'styled-components';

import { InvisibleButton } from '@components/atoms/button/Button';
import { ImageWithErrorHandling } from '@components/atoms/image-with-error-handling/ImageWithErrorHandling';
import { CenteredSpinner } from '@components/atoms/spinner/Spinner';
import {
  BodyText,
  CaptionSecondaryText,
  SectionText,
} from '@components/atoms/text/Text';
import { ClearSelectionButton } from '@components/media-filters/ClearSelectionButton';
import { SELECTED_PRODUCTS_LIMIT } from '@components/media-filters/ProductTagsFilter';
import {
  FilterOption,
  FilterSectionTitle,
  FilterValue,
} from '@components/media-filters/filterSectionModels';
import { Modal } from '@components/molecules/modal/Modal';
import {
  DEFAULT_PAGE_NUMBER,
  DEFAULT_PAGE_SIZE,
  TablePaginationControls,
} from '@components/molecules/pagination/TablePaginationControls';
import SearchBar from '@components/molecules/search-bar/SearchBar';
import { useProductsQuery } from '@content-drawer/filters/labels/hooks/useProductsQuery';
import {
  MEDIA_LIBRARY_PRODUCT_FILTER_PANEL_TRACKING_ID_PREFIX,
  MediaLibraryTrackingIds,
} from '@media-library/constants/mediaLibraryTrackingIds';
import { useProductCatalogsQuery } from '@uia/products/hooks/useProductCatalogsQuery';
import { isDefined } from '@utils/filterPredicates';

export interface AllProductsModalProps {
  initialSearchTerm: string;
  isOpen: boolean;
  onClose: () => void;
  options: FilterOption<string>[];
  updateSelectedProducts: (options: FilterOption<FilterValue>[]) => void;
}

export const AllProductsModal = ({
  initialSearchTerm,
  isOpen,
  onClose,
  options,
  updateSelectedProducts,
}: AllProductsModalProps) => {
  const [productOptions, setProductOptions] = useState(options);
  const [searchTerm, setSearchTerm] = useState<string>(initialSearchTerm);
  const [limit, setLimit] = useState<number>(DEFAULT_PAGE_SIZE);
  const [offset, setOffset] = useState<number>(DEFAULT_PAGE_NUMBER);

  const theme = useTheme();
  const { data: catalogs } = useProductCatalogsQuery();

  const { data: searchResults, isLoading } = useProductsQuery({
    hasCatalog: isDefined(catalogs) && catalogs.length > 0,
    limit: limit,
    offset: offset,
    search: searchTerm,
  });

  const selectedProducts = productOptions.filter((option) => option.isSelected);

  return (
    <Modal
      closeModal={onClose}
      contents={
        <ModalContents>
          <SearchBar
            onChange={(search) => {
              setSearchTerm(search);
              setOffset(DEFAULT_PAGE_NUMBER);
            }}
            placeholder={''}
            trackingIdPrefix={
              MEDIA_LIBRARY_PRODUCT_FILTER_PANEL_TRACKING_ID_PREFIX
            }
            value={searchTerm}
            visuallyHiddenLabel='Search products'
          />
          <ClearSelectionButton
            handleClearSection={setProductOptions}
            isDisabled={isLoading}
            options={productOptions}
            trackingId={MediaLibraryTrackingIds.PRODUCTS_CLEAR_SELECTION_BUTTON}
          />
          {isLoading ? (
            <CenteredSpinner />
          ) : (
            <ProductsContainer>
              <Products>
                {searchResults?.products.map((product) => (
                  <li key={product.productStyleId}>
                    <ProductContainer
                      $isSelected={isDefined(
                        productOptions.find(
                          (option) =>
                            option.value === product.productStyleId &&
                            option.isSelected,
                        ),
                      )}
                      onClick={() => {
                        if (isDefined(product.productStyleId)) {
                          const selectedOption = productOptions.find(
                            (option) => option.value === product.productStyleId,
                          );

                          if (!isDefined(selectedOption)) {
                            setProductOptions([
                              ...productOptions,
                              {
                                isSelected: true,
                                label: product.name ?? '',
                                value: product.productStyleId,
                              },
                            ]);
                          } else {
                            setProductOptions(
                              productOptions.map((option) => {
                                if (option.value === product.productStyleId) {
                                  return {
                                    ...option,
                                    isSelected: !selectedOption.isSelected,
                                  };
                                }
                                return option;
                              }),
                            );
                          }
                        }
                      }}
                      type='button'
                    >
                      <ProductContents>
                        <StyledImage
                          alt={product.name ?? 'Product Image'}
                          src={product.images[0]?.imageUrl}
                        />
                        <div>
                          <BodyText ellipsize={1} title={product.name}>
                            {product.name}
                          </BodyText>
                          <CaptionSecondaryText
                            ellipsize={1}
                            title={product.externalId}
                          >
                            {product.externalId}
                          </CaptionSecondaryText>
                        </div>
                      </ProductContents>
                    </ProductContainer>
                  </li>
                ))}
              </Products>
              {searchResults?.products.length === 0 && (
                <NoResultsContainer>
                  <SectionText>No products found</SectionText>
                  <BodyText>
                    Try searching again with different keywords.
                  </BodyText>
                </NoResultsContainer>
              )}
              <TablePaginationControlsWrapper>
                <TablePaginationControls
                  currentPage={Math.floor(offset / limit)}
                  handlePageChange={(
                    currentPage: number,
                    itemsPerPage: number,
                  ) => {
                    setLimit(itemsPerPage);
                    setOffset(currentPage * itemsPerPage);
                  }}
                  itemsPerPage={limit}
                  itemsPerPageOptions={[]}
                  numItems={searchResults?.totalCount ?? 0}
                />
              </TablePaginationControlsWrapper>
            </ProductsContainer>
          )}
        </ModalContents>
      }
      isModalOpen={isOpen}
      primaryButtonInfo={{
        buttonContent:
          selectedProducts.length > 0
            ? `Apply (${selectedProducts.length})`
            : 'Apply',
        isDisabled: selectedProducts.length > SELECTED_PRODUCTS_LIMIT,
        isTooltipDisabled: selectedProducts.length <= SELECTED_PRODUCTS_LIMIT,
        onClick: () => {
          updateSelectedProducts(productOptions);
          onClose();
        },
        tooltipContents: `Maximum ${SELECTED_PRODUCTS_LIMIT} product tags allowed for search.`,
        trackingId: MediaLibraryTrackingIds.PRODUCTS_APPLY_PRODUCTS,
      }}
      secondaryButtonInfo={{
        buttonContent: 'Cancel',
        onClick: onClose,
        trackingId: MediaLibraryTrackingIds.PRODUCTS_CLOSE_ALL_PRODUCTS_MODAL,
      }}
      title={FilterSectionTitle.productIds}
      width='wide'
      zIndexOverlayOverride={theme.zindex.modal} // to ensure the modal is displayed on top of the Content Drawer
    />
  );
};

const ModalContents = styled.div(
  ({ theme }) => `
    height: calc(${theme.spacings[5]} * 11);
  `,
);

const Products = styled.ul(
  ({ theme }) => `
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: ${theme.spacings[2]};
    padding: ${theme.spacings[2]} 0 0;
    list-style: none;
    margin: 0;
  `,
);

const ProductContainer = styled(InvisibleButton)<{
  $isSelected: boolean;
}>(
  ({ $isSelected, theme }) => `
    display: block;
    border: ${theme.borderWidths[1]} solid ${theme.colors.gray2};
    height: fit-content;

    && button {
      width: 100%;
      justify-content: start;
    }
    ${
      $isSelected &&
      `border: ${theme.borderWidths[2]} solid ${theme.colors.aliases.primary};`
    }
  `,
);

const StyledImage = styled(ImageWithErrorHandling)(
  ({ theme }) => `
    width: calc(${theme.spacings[5]} + ${theme.spacings[1]});
    height: calc(${theme.spacings[5]} + ${theme.spacings[1]});
    object-fit: contain;
  `,
);

const ProductContents = styled.div(
  ({ theme }) => `
    display: flex;
    gap: ${theme.spacings[2]};
    align-items: center;
    text-align: start;
    padding: ${theme.spacings[2]};
  `,
);

const TablePaginationControlsWrapper = styled.div(
  ({ theme }) => `
    padding: ${theme.spacings[2]} 0 ${theme.spacings[3]} 0;
  `,
);

const NoResultsContainer = styled(BodyText)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const ProductsContainer = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: calc(${theme.spacings[7]} * 5);
  `,
);
